import {
  Permission,
  PermissionSignature,
  Operation,
  ScopeType,
  ScopedOperations,
  ScopeIDMap,
  EntityIDMap,
} from "../types";
import { InitialEntityPermissionState } from "pages/permissions";

const applyScope = (
  ops: ScopedOperations,
  operation: Operation,
  scope: ScopeType,
): void => {
  ops.roleScope[operation] = scope === ScopeType.Role;
  ops.assignedScope[operation] = scope === ScopeType.AssignedToUser;
  ops.createdScope[operation] = scope === ScopeType.CreatedByUser;
};

export const calculateOperations = (
  roleScope: Permission | undefined,
  assignedScope: Permission | undefined,
  createdScope: Permission | undefined,
  handler: Operation,
  scope: ScopeType,
): ScopedOperations => {
  const ops: ScopedOperations = {
    roleScope: {
      create: roleScope?.create || false,
      read: roleScope?.read || false,
      update: roleScope?.update || false,
    },
    assignedScope: {
      create: assignedScope?.create || false,
      read: assignedScope?.read || false,
      update: assignedScope?.update || false,
    },
    createdScope: {
      create: createdScope?.create || false,
      read: createdScope?.read || false,
      update: createdScope?.update || false,
    },
  };

  if (roleScope?.entity.id === EntityIDMap.Permission) {
    if (handler === Operation.READ) {
      if (scope === ScopeType.Role) {
        ops.roleScope.read = true;
      } else {
        ops.roleScope.read = false;
        ops.roleScope.update = false;
      }
    }
    if (handler === Operation.UPDATE) {
      if (scope === ScopeType.Role) {
        ops.roleScope.read = true; //You can only turn off update permission if you have read rights
        ops.roleScope.update = true;
      } else {
        ops.roleScope.read = true; //You can only turn off update permission if you have read rights
        ops.roleScope.update = false;
      }
    }
  } else {
    switch (handler) {
      case Operation.READ:
        applyScope(ops, Operation.READ, scope);
        if (scope === ScopeType.None) {
          // read gates the entity, so losing it clears every other operation
          applyScope(ops, Operation.CREATE, ScopeType.None);
          applyScope(ops, Operation.UPDATE, ScopeType.None);
        }
        break;
      case Operation.CREATE:
        applyScope(ops, Operation.CREATE, scope);
        if (scope === ScopeType.Role) {
          ops.roleScope.read = true;
        }
        break;
      case Operation.UPDATE:
        applyScope(ops, Operation.UPDATE, scope);
        if (scope === ScopeType.Role) {
          ops.roleScope.read = true;
        }
        break;
    }
  }

  return ops;
};

export const getInitialPermissionState = (
  tenantPermissions: Array<Permission>,
  roleID: number,
  entityID: number,
  tenantID: number,
): InitialEntityPermissionState => {
  const roleScope = tenantPermissions.find((p) =>
    matchPermissions(p, {
      tenantID,
      roleID,
      entityID,
      scopeID: ScopeIDMap.Role,
    }),
  );
  const assignedScope = tenantPermissions.find((p) =>
    matchPermissions(p, {
      tenantID,
      roleID,
      entityID,
      scopeID: ScopeIDMap.AssignedToUser,
    }),
  );
  const createdScope = tenantPermissions.find((p) =>
    matchPermissions(p, {
      tenantID,
      roleID,
      entityID,
      scopeID: ScopeIDMap.CreatedByUser,
    }),
  );

  const initialState: InitialEntityPermissionState = {
    initialCreate: ScopeType.None,
    initialRead: ScopeType.None,
    initialUpdate: ScopeType.None,
  };
  // set initial read
  if (roleScope?.read) {
    initialState.initialRead = ScopeType.Role;
  } else if (assignedScope?.read) {
    initialState.initialRead = ScopeType.AssignedToUser;
  } else if (createdScope?.read) {
    initialState.initialRead = ScopeType.CreatedByUser;
  }
  // set initial update
  if (roleScope?.update) {
    initialState.initialUpdate = ScopeType.Role;
  } else if (assignedScope?.update) {
    initialState.initialUpdate = ScopeType.AssignedToUser;
  } else if (createdScope?.update) {
    initialState.initialUpdate = ScopeType.CreatedByUser;
  }
  // set initial create
  if (roleScope?.create) {
    initialState.initialCreate = ScopeType.Role;
  } else if (assignedScope?.create) {
    initialState.initialCreate = ScopeType.AssignedToUser;
  } else if (createdScope?.create) {
    initialState.initialCreate = ScopeType.CreatedByUser;
  }
  return initialState;
};

export const matchPermissions = (
  p1: Permission,
  p2: PermissionSignature,
): boolean => {
  if (
    p1.tenantID === p2.tenantID &&
    p1.role.id === p2.roleID &&
    p1.entity.id === p2.entityID &&
    p1.scope.id === p2.scopeID
  ) {
    return true;
  }
  return false;
};

export const matchPermissionsBySignature = (
  p1: PermissionSignature,
  p2: PermissionSignature,
): boolean => {
  if (
    p1.tenantID === p2.tenantID &&
    p1.roleID === p2.roleID &&
    p1.entityID === p2.entityID &&
    p1.scopeID === p2.scopeID
  ) {
    return true;
  }
  return false;
};

export const hasPermission = (
  p: Array<Permission>,
  tenantID: number,
  roleID: number,
  entityID: number,
  scopeID: number,
  o: Operation,
): boolean => {
  const permission = p.find((p) =>
    matchPermissions(p, {
      tenantID,
      roleID,
      entityID,
      scopeID,
    }),
  );

  if (!permission) return false;
  return permission[o];
};
